import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Package, Lock } from 'lucide-react';
import api from '@/lib/api';
import { formatRp, KATEGORI, KATEGORI_LABEL, SATUAN_OPTIONS } from '@/lib/format';
import { useAuth } from '@/context/AuthContext';
import { ListSkeleton } from '@/components/Skeletons';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';

const EMPTY = { kategori: 'profil', nama: '', merek: '', warna: '', satuan: 'batang', harga: '', panjang_batang: 6 };

export default function Katalog() {
  const { isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('profil');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/catalog'); setItems(r.data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = (kategori) => { setForm({ ...EMPTY, kategori, satuan: kategori === 'kaca' ? 'm2' : kategori === 'ongkos' ? 'm2' : kategori === 'aksesoris' ? 'pcs' : 'batang' }); setEditId(null); setOpen(true); };
  const openEdit = (it) => { setForm({ ...it, harga: it.harga, panjang_batang: it.panjang_batang || 6 }); setEditId(it.id); setOpen(true); };

  const save = async () => {
    if (!form.nama.trim()) { toast.error('Nama item wajib diisi'); return; }
    setSaving(true);
    const payload = { ...form, harga: Number(form.harga) || 0, panjang_batang: Number(form.panjang_batang) || 6 };
    try {
      if (editId) { await api.put(`/catalog/${editId}`, payload); toast.success('Item diperbarui'); }
      else { await api.post('/catalog', payload); toast.success('Item ditambahkan'); }
      setOpen(false); load();
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal menyimpan'); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Hapus item ini?')) return;
    try { await api.delete(`/catalog/${id}`); toast.success('Item dihapus'); load(); } catch { toast.error('Gagal menghapus'); }
  };

  const byKat = (k) => items.filter((i) => i.kategori === k);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-xl font-bold">Katalog & Harga</h1>
          <p className="text-sm text-muted-foreground">Sumber harga untuk semua penawaran & AI.</p>
        </div>
        {isOwner ? (
          <Button data-testid="katalog-tambah-button" onClick={() => openAdd(tab)} className="rounded-xl"><Plus className="mr-1.5 h-4 w-4" /> Tambah</Button>
        ) : (
          <span className="flex items-center gap-1 rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" /> Hanya owner</span>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-4">
          {KATEGORI.map((k) => <TabsTrigger key={k.value} value={k.value} className="text-xs">{k.label.split(' ')[0]}</TabsTrigger>)}
        </TabsList>
        {KATEGORI.map((k) => (
          <TabsContent key={k.value} value={k.value} className="mt-3">
            {loading ? <ListSkeleton /> : byKat(k.value).length === 0 ? (
              <EmptyState icon={Package} title={`Belum ada ${k.label}`} description={isOwner ? 'Tambah item agar AI bisa menghitung harga.' : 'Owner belum menambahkan item.'} actionLabel={isOwner ? 'Tambah Item' : undefined} onAction={() => openAdd(k.value)} />
            ) : (
              <div className="space-y-2.5">
                {byKat(k.value).map((it) => (
                  <div key={it.id} className="flex items-center gap-3 rounded-xl border bg-card p-3.5">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold">{it.nama}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {[it.merek, it.warna].filter(Boolean).join(' · ') || KATEGORI_LABEL[it.kategori]}
                        {it.kategori === 'profil' && it.panjang_batang ? ` · ${it.panjang_batang}m/batang` : ''}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-semibold tabular-nums">{formatRp(it.harga)}</div>
                      <div className="text-xs text-muted-foreground">/ {it.satuan}</div>
                    </div>
                    {isOwner && (
                      <div className="flex shrink-0 gap-1">
                        <button onClick={() => openEdit(it)} className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => remove(it.id)} className="flex h-9 w-9 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editId ? 'Edit Item' : 'Tambah Item Katalog'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Kategori</Label>
              <Select value={form.kategori} onValueChange={(v) => setForm({ ...form, kategori: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{KATEGORI.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Nama / Tipe *</Label><Input data-testid="katalog-form-nama" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="cth: Sliding 3 inch YKK" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Merek</Label><Input value={form.merek} onChange={(e) => setForm({ ...form, merek: e.target.value })} placeholder="YKK / Alexindo" /></div>
              <div className="space-y-1.5"><Label>Warna</Label><Input value={form.warna} onChange={(e) => setForm({ ...form, warna: e.target.value })} placeholder="Coklat / Silver" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Satuan</Label>
                <Select value={form.satuan} onValueChange={(v) => setForm({ ...form, satuan: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{SATUAN_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Harga (Rp) *</Label><Input data-testid="katalog-form-harga" type="number" value={form.harga} onChange={(e) => setForm({ ...form, harga: e.target.value })} placeholder="0" /></div>
            </div>
            {form.kategori === 'profil' && (
              <div className="space-y-1.5"><Label>Panjang Batang (m)</Label><Input type="number" value={form.panjang_batang} onChange={(e) => setForm({ ...form, panjang_batang: e.target.value })} placeholder="6" /></div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="katalog-form-simpan" onClick={save} disabled={saving} className="rounded-xl">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
